import drawInlineUserContent from '../drawInlineUserContent';
import findInlineUserContent from '../findInlineUserContent';
import { annotationColors } from 'constants';
import {
  UserContentCopiedText,
  UserContentHighlight,
  UserContentItem,
  UserContentNote,
} from 'types';
import isoStringDisplay from 'utilities/isoStringDisplay';

const insertInlineUserContent = (
  userContentItem,
  documentValuesContext,
  action = null,
  inlineUserContent = null,
) => {
  const { frameDocument, getRangeForCfi } = documentValuesContext;

  // Find existing spans and button for the user content item if they exist.
  inlineUserContent =
    inlineUserContent ||
    findInlineUserContent(frameDocument, userContentItem.uuid);
  let { buttonElement, highlightElement, spanStartElement, spanEndElement } =
    inlineUserContent ?? {};

  // If spans and button are not present, create and insert them.
  if (!inlineUserContent) {
    let kebabRepresentation = '';
    let capitalizedRepresentation = '';

    if (userContentItem instanceof UserContentHighlight) {
      kebabRepresentation = 'highlight';
      capitalizedRepresentation = 'Highlight';
    } else if (userContentItem instanceof UserContentNote) {
      kebabRepresentation = 'note';
      capitalizedRepresentation = 'Note';
    } else if (userContentItem instanceof UserContentCopiedText) {
      kebabRepresentation = 'copied-text';
      capitalizedRepresentation = 'Copied text';
    } else if (userContentItem instanceof UserContentItem) {
      console.error(
        'This function does not support non-highlight non-copiedtext user content.',
      );
      return;
    } else {
      console.error('Non-user-content object received.');
      return;
    }

    const uuid = userContentItem.uuid;
    const cfi = userContentItem.bookLocation.cfi;
    const color = annotationColors[userContentItem?.color] || '';
    const buttonText = `${color} ${capitalizedRepresentation} created at ${isoStringDisplay(
      userContentItem.dateCreated,
    )}`;

    const temporaryContainer = frameDocument.createElement('div');

    temporaryContainer.innerHTML = `<span
  xmlns="http://www.w3.org/1999/xhtml"
  class="rs-inline-boundary rs-inline-boundary-start"
  data-rs-uuid="${uuid}"
  data-rs-cfi="${cfi}"
  tabIndex="0"
  aria-label="${color} ${capitalizedRepresentation} start"
></span>
<span
  xmlns="http://www.w3.org/1999/xhtml"
  class="rs-inline-boundary rs-inline-boundary-end"
  data-rs-uuid="${uuid}"
  data-rs-cfi="${cfi}"
  tabIndex="0"
  aria-label="${color} ${capitalizedRepresentation} end"
></span>
<button
  xmlns="http://www.w3.org/1999/xhtml"
  class="rs-inline-button rs-inline-button-${kebabRepresentation}"
  data-rs-uuid="${uuid}"
  data-rs-cfi="${cfi}"
>${buttonText}</button>
<span
  xmlns="http://www.w3.org/1999/xhtml"
  aria-hidden="true"
  class="rs-inline-display rs-inline-display-${kebabRepresentation}"
  data-rs-uuid="${uuid}"
  data-rs-cfi="${cfi}"
></span>`;

    if (!cfi) {
      console.warn('No cfi for user content, skipping inline render.');
      return;
    }

    // Insert spans.
    let range;
    try {
      range = getRangeForCfi(cfi);
    } catch (error) {
      console.log('Not rendering annotation with bad CFI:', cfi, error);
      return;
    }

    if (range.startContainer.nodeType === Node.DOCUMENT_NODE) {
      console.warn(
        'Skipping rendering inline user content because the range includes the document itself.',
      );
      return;
    }

    spanStartElement = temporaryContainer.querySelector(
      '.rs-inline-boundary-start',
    );
    spanEndElement = temporaryContainer.querySelector(
      '.rs-inline-boundary-end',
    );
    highlightElement = temporaryContainer.querySelector('.rs-inline-display');
    buttonElement = temporaryContainer.querySelector('.rs-inline-button');

    range.insertNode(spanStartElement);
    range.insertNode(buttonElement);
    range.collapse(false);
    range.insertNode(spanEndElement);

    // We can normalize the parent elements for the new spans to trim "" text nodes (aids in testing)
    range.startContainer.parentElement?.normalize();
    range.endContainer.parentElement?.normalize();

    frameDocument.body.parentElement.appendChild(highlightElement);
  }

  // Apply events to elements.
  // Use on* instead of event listeners so we can override on the same elements later.
  const actionButRequireEnterKey = action
    ? (event) => event.keyCode === 13 && action()
    : null;

  buttonElement.onclick = action;
  buttonElement.oncontextmenu = action;
  buttonElement.onkeypress = actionButRequireEnterKey;
  buttonElement.onfocus = () => {
    highlightElement.classList.add('highlight-focused');
  };
  buttonElement.onblur = () => {
    highlightElement.classList.remove('highlight-focused');
  };

  spanStartElement.onclick = action;
  spanStartElement.oncontextmenu = action;
  spanStartElement.onkeypress = actionButRequireEnterKey;

  spanEndElement.onclick = action;
  spanEndElement.oncontextmenu = action;
  spanEndElement.onkeypress = actionButRequireEnterKey;

  highlightElement.onclick = action;
  highlightElement.oncontextmenu = action;
  highlightElement.onkeypress = actionButRequireEnterKey;

  // For screen readers: focus the associated button when tabbing reaches a span.
  spanStartElement.onfocus = action ? buttonElement.focus : null;
  spanEndElement.onfocus = action ? buttonElement.focus : null;
  highlightElement.onfocus = action ? buttonElement.focus : null;

  // Draw (or re-draw) highlight overlay.
  drawInlineUserContent(frameDocument, userContentItem, {
    buttonElement,
    highlightElement,
    spanStartElement,
    spanEndElement,
  });

  return {
    buttonElement,
    highlightElement,
    spanStartElement,
    spanEndElement,
  };
};

export default insertInlineUserContent;
